import {
  Badge,
  Box,
  Button,
  Container,
  Grid,
  HStack,
  Icon,
  Text,
  VStack,
  useDisclosure,
  Spinner
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  FiArrowLeft,
  FiCheck,
  FiCopy,
  FiEdit,
  FiInfo,
  FiLoader,
  FiSave
} from "react-icons/fi";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  mockApi,
  type CreateLinkData,
  type Link as LinkType
} from "../api/mockApi";
import {
  AdvancedSettings,
  CampaignDetails,
  DestinationSection,
  FilteredDestinationSection,
  DomainSelection,
  LinkConfiguration,
  LinkPreview,
  PlatformSelection,
  type DestinationConfig
} from "../components/CreateLink";

interface FormData {
  platform: string;
  redirectUrl: string;
  linkName: string;
  adSetName: string;
  adName: string;
  customId: string;
  metadata: string;
  forceExternalBrowser: boolean;
  domain: string;
  customQueryParams: string;
  destinationType: "single" | "ab-test" | "variant" | "custom";
  customRedirectUrl: string;
  destinationPath: string;
  destinationPathA: string;
  destinationPathB: string;
  abTestSplit: number;
  destinationVariant: string;
  abTestPaths: Array<{
    id: string;
    type: "path" | "variant";
    value: string;
    percentage: number;
  }>;
  abTestSchedule: {
    enabled: boolean;
    startDate?: string;
    endDate?: string;
  };
  // New filtered destinations
  destinations: DestinationConfig[];
  useFilteredDestinations: boolean;
}

export default function EditLink() {
  const { id } = useParams<{ id: string }>();
  const linkId = id || "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<FormData>({
    platform: "",
    redirectUrl: "",
    linkName: "",
    adSetName: "",
    adName: "",
    customId: "",
    metadata: "",
    forceExternalBrowser: false,
    domain: "try.zown.ca",
    customQueryParams: "",
    destinationType: "single",
    destinationPath: "",
    destinationPathA: "",
    destinationPathB: "",
    abTestSplit: 50,
    destinationVariant: "",
    customRedirectUrl: "",
    abTestPaths: [
      { id: "1", type: "path", value: "", percentage: 50 },
      { id: "2", type: "path", value: "", percentage: 50 }
    ],
    abTestSchedule: {
      enabled: false,
      startDate: "",
      endDate: ""
    },
    // New filtered destinations with default
    destinations: [
      {
        id: "default",
        isDefault: true,
        filters: {},
        destinationType: "single",
        destinationPath: "",
        isExpanded: true
      }
    ],
    useFilteredDestinations: true // Default to using the new system
  });

  const [customIdAvailable, setCustomIdAvailable] = useState<boolean | null>(
    null
  );
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [updatedLink, setUpdatedLink] = useState<LinkType | null>(null);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const { open, onOpen, onClose } = useDisclosure();

  // Fetch link data
  const {
    data: link,
    isLoading: isLoadingData,
    error: fetchError
  } = useQuery({
    queryKey: ["link", linkId],
    queryFn: () => mockApi.getLink(linkId),
    enabled: !!linkId
  });

  // Update link mutation
  const updateLinkMutation = useMutation({
    mutationFn: (data: Partial<CreateLinkData>) =>
      mockApi.updateLink(linkId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["links"] });
      queryClient.invalidateQueries({ queryKey: ["link", linkId] });
      setUpdatedLink(data);
      onOpen();

      // Auto-copy to clipboard
      const linkUrl = getLinkUrl(
        data.customId || data.id || "",
        formData.domain
      );
      navigator.clipboard.writeText(linkUrl);
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    }
  });

  // Generate the full URL for a link
  const getLinkUrl = (id: string, domain: string = "try.zown.ca") => {
    return `https://${domain}/${id}`;
  };

  // Update form data when link is loaded
  useEffect(() => {
    if (link) {
      console.log("Loading link data:", link); // Debug log
      console.log("Link domain value:", link.domain); // Specific domain debug
      
      // Check if link has new destinations array (stored in metadata or destinations field)
      const hasFilteredDestinations = (link as any).destinations || (link.metadata && (link.metadata as any).destinations);
      const destinations = hasFilteredDestinations 
        ? ((link as any).destinations || (link.metadata as any).destinations)
        : [
            {
              id: "default",
              isDefault: true,
              filters: {},
              destinationType: link.destinationType ?? "single",
              destinationPath: link.destinationPath ?? "",
              destinationVariant: link.destinationVariant ?? "",
              customRedirectUrl: link.customRedirectUrl ?? "",
              abTestPaths: link.abTestPaths ?? [
                { id: "1", type: "path", value: "", percentage: 50 },
                { id: "2", type: "path", value: "", percentage: 50 }
              ],
              abTestSchedule: link.abTestSchedule ?? {
                enabled: false,
                startDate: "",
                endDate: ""
              },
              isExpanded: true
            }
          ];
      
      // Handle nullable fields properly
      const formDataToSet = {
        platform: link.platform ?? "",
        redirectUrl: link.redirectUrl ?? "",
        linkName: link.linkName ?? "",
        adSetName: link.adSetName ?? "",
        adName: link.adName ?? "",
        customId: link.customId ?? "",
        metadata: link.metadata ? JSON.stringify(link.metadata, null, 2) : "",
        forceExternalBrowser: link.forceExternalBrowser ?? false,
        domain: link.domain ?? "try.zown.ca",
        customQueryParams: link.customQueryParams ?? "",
        destinationType: link.destinationType ?? "single",
        destinationPath: link.destinationPath ?? "",
        destinationPathA: link.destinationPathA ?? "",
        destinationPathB: link.destinationPathB ?? "",
        abTestSplit: link.abTestSplit ?? 50,
        destinationVariant: link.destinationVariant ?? "",
        customRedirectUrl: link.customRedirectUrl ?? "",
        abTestPaths: link.abTestPaths ?? [
          { id: "1", type: "path", value: "", percentage: 50 },
          { id: "2", type: "path", value: "", percentage: 50 }
        ],
        abTestSchedule: link.abTestSchedule ?? {
          enabled: false,
          startDate: "",
          endDate: ""
        },
        destinations: destinations,
        useFilteredDestinations: hasFilteredDestinations ? true : true // Always use new system for editing
      };
      
      console.log("Setting form data:", formDataToSet); // Debug log
      setFormData(formDataToSet);
      
      // Set custom ID availability to true since it's an existing link
      if (link.customId) {
        setCustomIdAvailable(true);
      }
    }
  }, [link]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let linkData: Partial<CreateLinkData>;

    if (formData.useFilteredDestinations) {
      // Validate destinations
      const defaultDestination = formData.destinations.find(d => d.isDefault);
      if (!defaultDestination) {
        setMetadataError("At least one default destination is required");
        return;
      }

      // Validate each destination's custom URL if needed
      for (const dest of formData.destinations) {
        if (dest.destinationType === "custom") {
          if (!dest.customRedirectUrl) {
            setMetadataError("Custom redirect URL is required for custom destinations");
            return;
          }
          
          try {
            const url = new URL(dest.customRedirectUrl);
            if (!['http:', 'https:'].includes(url.protocol)) {
              setMetadataError("Custom URL must start with http:// or https://");
              return;
            }
          } catch (error) {
            setMetadataError("Invalid custom redirect URL format");
            return;
          }
        }
      }

      // Use the default destination for the primary link data
      linkData = {
        platform: formData.platform,
        redirectUrl: formData.redirectUrl || "",
        linkName: formData.linkName || undefined,
        adSetName: formData.adSetName || undefined,
        adName: formData.adName || undefined,
        customId: formData.customId || undefined,
        metadata: formData.metadata ? JSON.parse(formData.metadata) : undefined,
        forceExternalBrowser: formData.forceExternalBrowser,
        domain: formData.domain,
        customQueryParams: formData.customQueryParams || undefined,
        destinationType: defaultDestination.destinationType,
        destinationPath:
          defaultDestination.destinationType === "single"
            ? defaultDestination.destinationPath
            : undefined,
        destinationVariant:
          defaultDestination.destinationType === "variant"
            ? defaultDestination.destinationVariant
            : undefined,
        customRedirectUrl:
          defaultDestination.destinationType === "custom"
            ? defaultDestination.customRedirectUrl
            : undefined,
        abTestPaths:
          defaultDestination.destinationType === "ab-test"
            ? defaultDestination.abTestPaths
            : undefined,
        abTestSchedule:
          defaultDestination.destinationType === "ab-test"
            ? defaultDestination.abTestSchedule
            : undefined,
        // Store full destinations array in metadata for now
        destinations: formData.destinations
      } as any;
    } else {
      // Legacy validation
      if (formData.destinationType === "custom") {
        if (!formData.customRedirectUrl) {
          setMetadataError("Custom redirect URL is required");
          return;
        }
        
        try {
          const url = new URL(formData.customRedirectUrl);
          if (!['http:', 'https:'].includes(url.protocol)) {
            setMetadataError("Custom URL must start with http:// or https://");
            return;
          }
        } catch (error) {
          setMetadataError("Invalid custom redirect URL format");
          return;
        }
      }

      linkData = {
        platform: formData.platform,
        redirectUrl: formData.redirectUrl || "",
        linkName: formData.linkName || undefined,
        adSetName: formData.adSetName || undefined,
        adName: formData.adName || undefined,
        customId: formData.customId || undefined,
        metadata: formData.metadata ? JSON.parse(formData.metadata) : undefined,
        forceExternalBrowser: formData.forceExternalBrowser,
        domain: formData.domain,
        customQueryParams: formData.customQueryParams || undefined,
        destinationType: formData.destinationType,
        destinationPath:
          formData.destinationType === "single"
            ? formData.destinationPath
            : undefined,
        destinationPathA:
          formData.destinationType === "ab-test"
            ? formData.destinationPathA
            : undefined,
        destinationPathB:
          formData.destinationType === "ab-test"
            ? formData.destinationPathB
            : undefined,
        abTestSplit:
          formData.destinationType === "ab-test"
            ? formData.abTestSplit
            : undefined,
        destinationVariant:
          formData.destinationType === "variant"
            ? formData.destinationVariant
            : undefined,
        customRedirectUrl:
          formData.destinationType === "custom"
            ? formData.customRedirectUrl
            : undefined,
        abTestPaths:
          formData.destinationType === "ab-test"
            ? formData.abTestPaths
            : undefined,
        abTestSchedule:
          formData.destinationType === "ab-test"
            ? formData.abTestSchedule
            : undefined
      };
    }

    // Validate metadata if provided
    if (formData.metadata) {
      try {
        JSON.parse(formData.metadata);
      } catch (error) {
        setMetadataError("Invalid JSON format");
        return;
      }
    }

    updateLinkMutation.mutate(linkData);
  };

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCampaignChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMetadataChange = (value: string, error: string | null) => {
    setFormData((prev) => ({ ...prev, metadata: value }));
    setMetadataError(error);
  };

  const handleCustomIdChange = (value: string, isAvailable: boolean | null) => {
    setFormData((prev) => ({ ...prev, customId: value }));
    setCustomIdAvailable(isAvailable);
  };

  const handleGoToLinks = () => {
    navigate("/links");
  };

  const copyToClipboard = () => {
    if (updatedLink) {
      const linkUrl = getLinkUrl(
        updatedLink.customId || updatedLink.id || "",
        formData.domain
      );
      navigator.clipboard.writeText(linkUrl);
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    }
  };

  if (isLoadingData) {
    return (
      <Box bg="gray.50" minH="100vh">
        <Container maxW="6xl" py={8}>
          <Box textAlign="center">
            <Spinner size="lg" color="blue.500" />
            <Text fontSize="lg" color="gray.600" mt={4}>
              Loading link data...
            </Text>
          </Box>
        </Container>
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Box bg="gray.50" minH="100vh">
        <Container maxW="6xl" py={8}>
          <Box textAlign="center">
            <Box
              p={4}
              bg="red.50"
              border="1px"
              borderColor="red.200"
              rounded="md"
              mb={4}
            >
              <Text color="red.600" fontWeight="medium">
                Error loading link
              </Text>
              <Text color="red.500" fontSize="sm">
                {fetchError.message}
              </Text>
            </Box>
            <Link to="/links">
              <Button variant="ghost" size="sm" _hover={{ bg: "gray.100" }}>
                <FiArrowLeft />
                Back to Links
              </Button>
            </Link>
          </Box>
        </Container>
      </Box>
    );
  }

  if (!link) {
    return (
      <Box bg="gray.50" minH="100vh">
        <Container maxW="6xl" py={8}>
          <Box textAlign="center">
            <Text fontSize="lg" color="red.600" mb={4}>
              Link not found
            </Text>
            <Link to="/links">
              <Button variant="ghost" size="sm" _hover={{ bg: "gray.100" }}>
                <FiArrowLeft />
                Back to Links
              </Button>
            </Link>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg="gray.50" minH="100vh">
      <Container maxW="6xl" py={8}>
        {/* Header */}
        <VStack gap={6} align="stretch" mb={8}>
          <HStack justify="space-between" align="center">
            <HStack gap={3}>
              <Link to="/links">
                <Button variant="ghost" size="sm" _hover={{ bg: "gray.100" }}>
                  <FiArrowLeft />
                  Back to Links
                </Button>
              </Link>
              <Text color="gray.500">/</Text>
              <Text color="gray.700" fontWeight="medium">
                Edit Link: {link.customId || link.id}
              </Text>
            </HStack>
            <Badge colorScheme="purple" px={3} py={1} rounded="full">
              <HStack gap={1}>
                <Icon as={FiEdit} />
                <Text>Edit Mode</Text>
              </HStack>
            </Badge>
          </HStack>
        </VStack>

        <Grid templateColumns={{ base: "1fr", lg: "1fr 400px" }} gap={8}>
          {/* Main Form */}
          <VStack as="form" onSubmit={handleSubmit} gap={6} align="stretch">
            {/* Domain Selection */}
            <DomainSelection
              domain={formData.domain}
              onChange={(domain) => handleFieldChange("domain", domain)}
              disabled={updateLinkMutation.isPending}
            />

            {/* Platform Selection */}
            <PlatformSelection
              platform={formData.platform}
              onChange={(platform) => handleFieldChange("platform", platform)}
              disabled={updateLinkMutation.isPending}
            />

            {/* Campaign Details */}
            <CampaignDetails
              linkName={formData.linkName}
              adSetName={formData.adSetName}
              adName={formData.adName}
              onChange={handleCampaignChange}
              disabled={updateLinkMutation.isPending}
            />

            {/* Destination - New Filtered System */}
            {formData.useFilteredDestinations ? (
              <FilteredDestinationSection
                domain={formData.domain}
                destinations={formData.destinations}
                onChange={(destinations) => handleFieldChange("destinations", destinations)}
                disabled={updateLinkMutation.isPending}
              />
            ) : (
              /* Legacy Destination Section (for backward compatibility) */
              <DestinationSection
                domain={formData.domain}
                destinationType={formData.destinationType}
                destinationPath={formData.destinationPath}
                destinationVariant={formData.destinationVariant}
                customRedirectUrl={formData.customRedirectUrl}
                abTestPaths={formData.abTestPaths}
                abTestSchedule={formData.abTestSchedule as { enabled: boolean; startDate: string; endDate: string; }}
                onChange={handleFieldChange}
                disabled={updateLinkMutation.isPending}
              />
            )}

            {/* Link Configuration */}
            <LinkConfiguration
              redirectUrl={formData.redirectUrl}
              customId={formData.customId}
              onRedirectUrlChange={(value) =>
                handleFieldChange("redirectUrl", value)
              }
              onCustomIdChange={handleCustomIdChange}
              disabled={updateLinkMutation.isPending}
            />

            {/* Advanced Settings */}
            <AdvancedSettings
              metadata={formData.metadata}
              metadataError={metadataError}
              forceExternalBrowser={formData.forceExternalBrowser}
              customQueryParams={formData.customQueryParams}
              redirectUrl={formData.redirectUrl}
              onMetadataChange={handleMetadataChange}
              onForceExternalBrowserChange={(value) =>
                handleFieldChange("forceExternalBrowser", value)
              }
              onCustomQueryParamsChange={(value) =>
                handleFieldChange("customQueryParams", value)
              }
              disabled={updateLinkMutation.isPending}
            />

            {/* Submit Button */}
            <Button
              type="submit"
              colorScheme="blue"
              size="lg"
              disabled={
                updateLinkMutation.isPending ||
                !formData.platform ||
                customIdAvailable === false ||
                !!metadataError
              }
              width="full"
            >
              <HStack gap={2}>
                {updateLinkMutation.isPending ? (
                  <>
                    <Icon as={FiLoader} className="animate-spin" />
                    <Text>Updating Link...</Text>
                  </>
                ) : (
                  <>
                    <Icon as={FiSave} />
                    <Text>Update Link</Text>
                  </>
                )}
              </HStack>
            </Button>
          </VStack>

          {/* Sidebar */}
          <VStack gap={6} align="stretch">
            {/* Link Preview */}
            <LinkPreview
              platform={formData.platform}
              linkName={formData.linkName}
              adSetName={formData.adSetName}
              adName={formData.adName}
              destinationType={formData.destinationType as "single" | "ab-test" | "variant"}
              destinationPath={formData.destinationPath}
              destinationVariant={formData.destinationVariant}
              abTestPaths={formData.abTestPaths}
              abTestSchedule={formData.abTestSchedule}
              forceExternalBrowser={formData.forceExternalBrowser}
              customQueryParams={formData.customQueryParams}
              generatedUrl={
                formData.customId
                  ? getLinkUrl(formData.customId, formData.domain)
                  : link.customId
                  ? getLinkUrl(link.customId, formData.domain)
                  : getLinkUrl(link.id || "", formData.domain)
              }
              domain={formData.domain}
              customId={formData.customId}
              redirectUrl={formData.redirectUrl}
            />

            {/* Link Info Card */}
            <Box
              bg="white"
              shadow="sm"
              borderColor="gray.200"
              border="1px"
              rounded="lg"
              p={6}
            >
              <HStack mb={3}>
                <Icon as={FiInfo} color="gray.600" boxSize={5} />
                <Text fontWeight="semibold" color="gray.900">
                  Link Information
                </Text>
              </HStack>

              <VStack align="stretch" gap={2} fontSize="sm" color="gray.600">
                <Text>
                  <strong>ID:</strong> {link.id || "N/A"}
                </Text>
                <Text>
                  <strong>Created:</strong>{" "}
                  {link.created
                    ? new Date(link.created).toLocaleString()
                    : "N/A"}
                </Text>
                <Text>
                  <strong>Last Updated:</strong>{" "}
                  {link.updated
                    ? new Date(link.updated).toLocaleString()
                    : "Never"}
                </Text>
              </VStack>
            </Box>

            {/* Tips Card */}
            <Box
              bg="blue.50"
              shadow="sm"
              borderColor="blue.200"
              border="1px"
              rounded="lg"
              p={6}
            >
              <HStack mb={3}>
                <Icon as={FiInfo} color="blue.600" boxSize={5} />
                <Text fontWeight="semibold" color="blue.900">
                  Update Tips
                </Text>
              </HStack>

              <VStack align="stretch" gap={2} fontSize="sm" color="blue.800">
                <Text>• Changes will take effect immediately</Text>
                <Text>• The tracking link URL will remain the same</Text>
                <Text>• Historical click data is preserved</Text>
                <Text>• Update metadata to track changes</Text>
              </VStack>
            </Box>
          </VStack>
        </Grid>

        {/* Success Dialog */}
        {open && (
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.600"
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={9999}
          >
            <Box
              bg="white"
              rounded="lg"
              shadow="xl"
              p={8}
              maxW="md"
              width="90%"
            >
              <VStack gap={4} align="center">
                <Box
                  bg="green.100"
                  rounded="full"
                  p={3}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={FiCheck} color="green.600" boxSize={8} />
                </Box>
                <Text fontSize="2xl" fontWeight="bold">
                  Link Updated Successfully!
                </Text>
                <Text color="gray.600" textAlign="center">
                  Your tracking link has been updated with the new settings.
                </Text>

                {updatedLink && (
                  <Box
                    bg="gray.50"
                    p={4}
                    rounded="md"
                    width="100%"
                    border="1px"
                    borderColor="gray.200"
                  >
                    <Text fontSize="sm" color="gray.600" mb={2}>
                      Your tracking link:
                    </Text>
                    <HStack gap={2}>
                      <Text
                        fontFamily="mono"
                        fontSize="sm"
                        flex={1}
                        wordBreak="break-all"
                      >
                        {getLinkUrl(
                          updatedLink.customId || updatedLink.id || "",
                          formData.domain
                        )}
                      </Text>
                      <Button size="sm" onClick={copyToClipboard}>
                        <HStack gap={1}>
                          <Icon as={FiCopy} />
                          <Text>{copiedToClipboard ? "Copied!" : "Copy"}</Text>
                        </HStack>
                      </Button>
                    </HStack>
                  </Box>
                )}

                <HStack gap={3} width="full" mt={6}>
                  <Button variant="outline" onClick={onClose} flex={1}>
                    Continue Editing
                  </Button>
                  <Button
                    colorScheme="blue"
                    onClick={handleGoToLinks}
                    flex={1}
                  >
                    View All Links
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
}

// Add spinning animation
const style = document.createElement("style");
style.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .animate-spin {
    animation: spin 1s linear infinite;
  }
`;
document.head.appendChild(style);